// uiarc: menü/palet vurgusu (dropdown-menu `highlight`, command-palette `highlight`). Tek bir yüzey, etkin öğenin
// (`data-highlighted` / `data-selected`) arkasında kayar: işaretçiyle yayla (`snappy`) süzülür, klavyeyle anında
// atlar; ilk göründüğünde kısa solar. Öğeler ilk çocuk olan bu öğeden sonra gelir ve `relative` olmalıdır;
// kapsayıcı (parentElement) `relative` olmalıdır. `prefers-reduced-motion` altında süzülme yok.
import { animate, motion, useMotionValue, useReducedMotion } from 'motion/react'
import { useEffect, useRef } from 'react'
import { motionTokens } from '@/lib/motion'
import { cn } from '@/lib/utils'

/** Öğenin kapsayıcıya göre üst konumu (dönüşümlerden etkilenmez: `offsetTop` toplamı). */
function offsetWithin(node: HTMLElement, container: HTMLElement) {
  let top = 0
  for (
    let element: HTMLElement | null = node;
    element && element !== container;
    element = element.offsetParent as HTMLElement | null
  )
    top += element.offsetTop + (element === node ? 0 : element.clientTop)
  return top
}

function GlideHighlight({
  active = '[data-highlighted]',
  className,
}: {
  /** Etkin öğeyi bulan CSS seçici. */
  active?: string
  className?: string
}) {
  const reduced = useReducedMotion()
  const ref = useRef<HTMLSpanElement>(null)
  const y = useMotionValue(0)
  const height = useMotionValue(0)
  const opacity = useMotionValue(0)

  useEffect(() => {
    const span = ref.current
    const container = span?.parentElement
    if (!span || !container) return
    let shown = false
    let pointer = false
    let clearTimer = 0
    const update = () => {
      const item = container.querySelector<HTMLElement>(active)
      window.clearTimeout(clearTimer)
      if (!item) {
        // İşaretçi öğeler arası boşlukta gezerken vurgu kısa süre kalır; klavyede hemen söner.
        clearTimer = window.setTimeout(
          () => {
            shown = false
            animate(opacity, 0, { duration: reduced ? 0 : 0.1 })
          },
          pointer ? 70 : 0,
        )
        return
      }
      const move = shown && pointer && !reduced ? motionTokens.spring.snappy : { duration: 0 }
      span.dataset.tone = item.dataset.variant === 'destructive' ? 'danger' : ''
      animate(y, offsetWithin(item, container), move)
      animate(height, item.offsetHeight, move)
      animate(opacity, 1, { duration: reduced ? 0 : 0.08 })
      shown = true
    }
    const mutations = new MutationObserver(update)
    mutations.observe(container, {
      attributes: true,
      attributeFilter: [active.replace(/^\[|(=.*)?\]$/g, '')],
      subtree: true,
      childList: true,
    })
    const onPointer = () => {
      pointer = true
    }
    const onKey = () => {
      pointer = false
    }
    container.addEventListener('pointermove', onPointer, true)
    container.addEventListener('keydown', onKey, true)
    update()
    return () => {
      window.clearTimeout(clearTimer)
      mutations.disconnect()
      container.removeEventListener('pointermove', onPointer, true)
      container.removeEventListener('keydown', onKey, true)
    }
  }, [active, reduced, y, height, opacity])

  return (
    <motion.span
      ref={ref}
      aria-hidden="true"
      data-slot="glide-highlight"
      style={{ y, height, opacity }}
      className={cn(
        'bg-surface-muted pointer-events-none absolute top-0 right-1.25 left-1.25 rounded-[calc(var(--radius-panel)-6px)] data-[tone=danger]:bg-[color-mix(in_oklab,var(--danger)_8%,var(--surface))]',
        className,
      )}
    />
  )
}

export { GlideHighlight }
